import React from "react";
import MetricBreakdownPopover from "./MetricBreakdownPopover";
import SparklineChart from "./SparklineChart";
import { getDeltaDisplay } from "./deltaDisplay";

/** A single metric row in the grouped table. */
export interface MetricRow {
  key: string;
  label: string;
  subtitle?: string;
  value: number | null;
  prevValue: number | null;
  formatFn: (v: number) => string;
  /** 12-month series (oldest → newest); omit to render an empty sparkline cell. */
  trend?: (number | null)[];
  /** "Lower is better" (churn / CAC / payback): increase = red. */
  invertDelta?: boolean;
  /**
   * Snapshot (point-in-time) metric — a missing delta is expected, so the cell
   * reads "snapshot" with an info tooltip rather than a bare "—".
   */
  isSnapshot?: boolean;
  /**
   * Optional CSS class string for the label `<div>`. When provided it fully
   * replaces the default `"font-medium text-neutral-800"` — use for sub-rows
   * that need indentation (`pl-8`) and muted colour (`text-neutral-500`).
   */
  labelClassName?: string;
  /**
   * When set, an info-badge (ⓘ) is rendered next to the label with this text
   * as tooltip. Distinct from `isSnapshot` (different tooltip, different trigger).
   */
  infoBadgeTitle?: string;
  /**
   * Key of the parent row this row is a collapsible child of (e.g. the CAC
   * per-channel sub-rows reference the `cacCents` parent). Rows with a
   * `parentKey` are hidden until their parent is expanded; a parent renders a
   * chevron toggle. Rows without one render normally.
   */
  parentKey?: string;
  breakdown?: {
    title: string;
    hasContent: boolean;
    content: React.ReactNode;
  };
}

/** A named block of related metric rows (e.g. "Pozyskanie"). */
export interface MetricGroup {
  title: string;
  rows: MetricRow[];
}

const SNAPSHOT_TOOLTIP =
  "Miara historyczna — bez porównania z poprzednim okresem";

export const InfoBadge: React.FC<{ title: string }> = ({ title }) => (
  <span
    className="ml-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-neutral-300 text-[9px] font-bold leading-none text-neutral-400"
    title={title}
    aria-label={title}
  >
    i
  </span>
);

/** Right-pointing chevron that rotates 90° when its row is expanded. */
const Chevron: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
    className={`h-3.5 w-3.5 shrink-0 text-neutral-400 transition-transform ${
      expanded ? "rotate-90" : ""
    }`}
  >
    <path
      fillRule="evenodd"
      d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z"
      clipRule="evenodd"
    />
  </svg>
);

/** Label text plus its optional snapshot / info badges (shared by all layouts). */
const LabelContent: React.FC<{ row: MetricRow }> = ({ row }) => (
  <>
    {row.label}
    {row.isSnapshot && <InfoBadge title={SNAPSHOT_TOOLTIP} />}
    {row.infoBadgeTitle && <InfoBadge title={row.infoBadgeTitle} />}
  </>
);

/**
 * The "Wskaźnik" cell. A parent row (one with collapsible children) renders a
 * chevron toggle button; every other row renders a static label `<div>` exactly
 * as before, so non-collapsible rows are visually unchanged.
 */
const LabelCell: React.FC<{
  row: MetricRow;
  isParent: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ row, isParent, isExpanded, onToggle }) => {
  const labelClass = row.labelClassName ?? "font-medium text-neutral-800";
  if (!isParent) {
    return (
      <div className={labelClass}>
        <LabelContent row={row} />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isExpanded}
      title={isExpanded ? "Zwiń podział na kanały" : "Rozwiń podział na kanały"}
      className="group flex w-full items-center gap-1.5 text-left"
    >
      <Chevron expanded={isExpanded} />
      <span className={labelClass}>
        <LabelContent row={row} />
      </span>
      <span className="ml-1 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-neutral-400 group-hover:bg-neutral-200">
        wg kanału
      </span>
    </button>
  );
};

/** A flattened, order-preserving render item produced from a group's rows. */
interface RenderItem {
  row: MetricRow;
  isParent: boolean;
  isExpanded: boolean;
}

const ValueCell: React.FC<{ row: MetricRow }> = ({ row }) => {
  const display = row.value === null ? "—" : row.formatFn(row.value);
  if (row.breakdown) {
    return (
      <MetricBreakdownPopover
        title={row.breakdown.title}
        hasContent={row.breakdown.hasContent}
        content={row.breakdown.content}
      >
        {display}
      </MetricBreakdownPopover>
    );
  }
  return <>{display}</>;
};

const DeltaCell: React.FC<{ row: MetricRow }> = ({ row }) => {
  if (row.isSnapshot) {
    return (
      <span className="text-xs italic text-neutral-400">snapshot</span>
    );
  }
  const delta = getDeltaDisplay(row.value, row.prevValue, row.invertDelta);
  return (
    <span
      className={`text-sm font-medium ${delta.colorClass}`}
      aria-label={delta.pct === null ? "brak danych do porównania" : undefined}
    >
      {delta.arrow && (
        <span aria-hidden="true" className="mr-0.5">
          {delta.arrow}
        </span>
      )}
      {delta.text}
    </span>
  );
};

/**
 * Period-over-period metrics, grouped into named blocks. On desktop it renders
 * a four-column table (Teraz | Poprzednio | Δ | trend); below `sm` it collapses
 * to stacked cards and the sparkline is hidden (M9). Reuses
 * {@link getDeltaDisplay} (→ `calcDeltaPct`) and the shared
 * `MetricBreakdownPopover` for drill-downs — no logic is reimplemented.
 *
 * Rows carrying a `parentKey` are collapsible children (e.g. the per-channel
 * CAC / LTV:CAC / Payback sub-rows): hidden until their parent's chevron is
 * toggled. Default is collapsed, so the table reads cleanly and the breakdown is
 * opt-in.
 */
const MetricsTable: React.FC<{
  groups: MetricGroup[];
  comparisonPeriodLabel?: string;
}> = ({ groups, comparisonPeriodLabel }) => {
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const toggle = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  /**
   * Flatten a group's rows into the visible render order: every top-level row,
   * each immediately followed by its children when expanded. Children (rows with
   * `parentKey`) are otherwise skipped.
   */
  const renderItems = (rows: MetricRow[]): RenderItem[] => {
    const childrenByParent = new Map<string, MetricRow[]>();
    for (const r of rows) {
      if (!r.parentKey) continue;
      const arr = childrenByParent.get(r.parentKey);
      if (arr) arr.push(r);
      else childrenByParent.set(r.parentKey, [r]);
    }
    const items: RenderItem[] = [];
    for (const r of rows) {
      if (r.parentKey) continue; // rendered under its parent
      const children = childrenByParent.get(r.key);
      const isParent = !!children && children.length > 0;
      const isExpanded = isParent && !!expanded[r.key];
      items.push({ row: r, isParent, isExpanded });
      if (isExpanded) {
        for (const c of children!) {
          items.push({ row: c, isParent: false, isExpanded: false });
        }
      }
    }
    return items;
  };

  return (
    <div className="rounded-lg border border-neutral-200 bg-white">
      {/* Desktop / tablet: table layout */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th
                scope="col"
                className="px-4 py-2 text-left text-xs font-medium uppercase text-neutral-500"
              >
                Wskaźnik
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-right text-xs font-medium uppercase text-neutral-500"
              >
                Teraz
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-right text-xs font-medium uppercase text-neutral-500"
              >
                Poprzednio
                {comparisonPeriodLabel ? (
                  <span className="block font-normal normal-case text-neutral-400">
                    {comparisonPeriodLabel}
                  </span>
                ) : null}
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-right text-xs font-medium uppercase text-neutral-500"
              >
                Δ
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-right text-xs font-medium uppercase text-neutral-500"
              >
                Trend 12 mies.
              </th>
            </tr>
          </thead>
          {groups.map((group) => (
            <tbody key={group.title} className="divide-y divide-neutral-100">
              <tr className="bg-neutral-50/60">
                <th
                  scope="colgroup"
                  colSpan={5}
                  className="px-4 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-neutral-600"
                >
                  {group.title}
                </th>
              </tr>
              {renderItems(group.rows).map(({ row, isParent, isExpanded }) => (
                <tr key={row.key} className="hover:bg-neutral-50">
                  <td className="px-4 py-2.5">
                    <LabelCell
                      row={row}
                      isParent={isParent}
                      isExpanded={isExpanded}
                      onToggle={() => toggle(row.key)}
                    />
                    {row.subtitle && (
                      <div className="text-xs text-neutral-400">
                        {row.subtitle}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-neutral-900">
                    <ValueCell row={row} />
                  </td>
                  <td className="px-4 py-2.5 text-right text-neutral-500">
                    {row.prevValue === null ? "—" : row.formatFn(row.prevValue)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <DeltaCell row={row} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="inline-flex justify-end">
                      <SparklineChart
                        values={row.trend ?? []}
                        formatFn={row.formatFn}
                        invertDelta={row.invertDelta}
                        metricLabel={row.label}
                      />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>

      {/* Mobile: stacked cards, sparkline hidden (M9) */}
      <div className="divide-y divide-neutral-100 sm:hidden">
        {groups.map((group) => (
          <div key={group.title}>
            <div className="bg-neutral-50/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-600">
              {group.title}
            </div>
            {renderItems(group.rows).map(({ row, isParent, isExpanded }) => (
              <div
                key={row.key}
                className="flex items-start justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <LabelCell
                    row={row}
                    isParent={isParent}
                    isExpanded={isExpanded}
                    onToggle={() => toggle(row.key)}
                  />
                  {row.subtitle && (
                    <div className="text-xs text-neutral-400">
                      {row.subtitle}
                    </div>
                  )}
                  <div className="mt-0.5 text-xs text-neutral-400">
                    Poprzednio:{" "}
                    {row.prevValue === null
                      ? "—"
                      : row.formatFn(row.prevValue)}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-semibold text-neutral-900">
                    <ValueCell row={row} />
                  </div>
                  <div className="mt-0.5">
                    <DeltaCell row={row} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MetricsTable;
